import React, { ReactNode } from 'react';

import { Banner, Box, Button, Dialog, H1, Text, VisuallyHidden } from '@betterment/samba-react';

import { useAdvisorsContactMail } from '../../hooks/useAdvisorsContactMail';
import { Acat } from '../../services/api/types/co_pilot/acats';
import MaskedAccountNumber from '../MaskedAccountNumber';

const PendingDetails: React.FC<React.PropsWithChildren<Pick<Acat, 'clientName' | 'transferDetails'>>> = ({
  clientName,
  transferDetails,
}) => {
  return (
    <>
      <Box>
        <Text color="text.weak">Below are the transfer details for {clientName}’s ACATS transfer.</Text>
      </Box>
      <Box css={{ my: 'space.300' }}>
        <Banner variant="info">
          <Banner.Content>{transferDetails}</Banner.Content>
        </Banner>
      </Box>
    </>
  );
};

interface FailureDetailsProps extends Pick<Acat, 'transferDetails'> {
  acatsError: ReactNode;
}

const FailureDetails: React.FC<React.PropsWithChildren<FailureDetailsProps>> = ({ transferDetails, acatsError }) => {
  return (
    <>
      <Box>
        <Banner variant="critical">
          <Banner.Content>{transferDetails}</Banner.Content>
        </Banner>
      </Box>
      <Box css={{ my: 'space.300' }}>{acatsError}</Box>
    </>
  );
};

export const AcatsModal: React.FC<
  React.PropsWithChildren<{ acat: Acat; closeHandler: () => void; status: string }>
> = ({
  acat: { clientName, description, accountNumber, transferringAssets, transferDetails },
  closeHandler,
  status,
}) => {
  const { acatsError } = useAdvisorsContactMail();

  return (
    <Dialog open={true} onOpenChange={closeHandler}>
      <Dialog.Content css={{ display: 'flex', flexDirection: 'column', p: 'space.400', gap: 'space.200' }} size="large">
        <Dialog.Title asChild={true}>
          <H1 variant="title300">Transfer details</H1>
        </Dialog.Title>
        <VisuallyHidden asChild={true}>
          <Dialog.Description>Transfer details for {clientName}</Dialog.Description>
        </VisuallyHidden>

        {status === 'pending' && <PendingDetails transferDetails={transferDetails} clientName={clientName} />}
        {status === 'failed' && <FailureDetails transferDetails={transferDetails} acatsError={acatsError} />}

        <Box css={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'space.200', mb: 'space.300' }}>
          <H1 variant="title100">Client</H1>
          <Box>{clientName}</Box>

          <H1 variant="title100">Description</H1>
          <Box>{description}</Box>

          <H1 variant="title100">Account number</H1>
          <Box>
            <MaskedAccountNumber accountNumber={accountNumber} />
          </Box>

          <H1 variant="title100">Transferring assets</H1>
          <Box>{transferringAssets}</Box>
        </Box>

        <Box>
          <Button css={{ px: 'space.600' }} onClick={() => closeHandler()}>
            Got it
          </Button>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};
